import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, RotateCw, TrendingUp, X } from 'lucide-react';
import { ExportFormatButtons } from './buttons/ExportFormatButton';
import { ArtifactPageTabs } from './ArtifactPageTabs';
import { ArtifactContent } from './ArtifactContent';

import { applyTheme } from '@/artifact/theme';
import { downloadExportPayload, exportSurfaceSnapshot, type ArtifactExportFormat } from '@/artifact/export';
import { trackArtifactExported } from '@/analytics';
import type { ArtifactEngine } from '@/hooks/useArtifactEngine';

import { ConnectionStatus } from './labels/ConnectionStatus';
import { ArtifactEmptyState } from './labels/ArtifactEmptyState';
import { IconFrame } from './layout/IconFrame';

import '@/artifact/artifact.css';

interface MobileArtifactSheetProps {
  engine: ArtifactEngine;
  artifactActivated: boolean;
  cardMinimized: boolean;
  onMinimize: () => void;
  overlayOpen: boolean;
  onOpenOverlay: () => void;
  onCloseOverlay: () => void;
}

export function MobileArtifactSheet({
  engine,
  artifactActivated,
  cardMinimized,
  onMinimize,
  overlayOpen,
  onOpenOverlay,
  onCloseOverlay,
}: MobileArtifactSheetProps) {
  const {
    store,
    connected,
    pages,
    viewingPageIndex,
    liveComponentsSize,
    rootCuids,
    focusTarget,
    hasComponents,
    hasPages,
    callbacks,
    theme,
    setSurfaceGetter,
    deletePage,
  } = engine;

  const [exportingFormat, setExportingFormat] = useState<ArtifactExportFormat | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getSurfaceElement = useCallback((): HTMLElement | null => {
    if (!containerRef.current) return null;
    const el = containerRef.current.querySelector('.a-surface');
    return el instanceof HTMLElement ? el : null;
  }, []);

  useEffect(() => {
    if (overlayOpen) {
      setSurfaceGetter(getSurfaceElement);
      return () => setSurfaceGetter(null);
    }
  }, [overlayOpen, getSurfaceElement, setSurfaceGetter]);

  useLayoutEffect(() => {
    if (containerRef.current) {
      applyTheme(theme, containerRef.current, true);
    }
  }, [theme, overlayOpen]);

  const componentCount = useMemo(() => {
    const comps = engine.effectiveComponents;
    let charts = 0;
    for (const node of comps.values()) {
      const t = node.component.toLowerCase();
      if (t.includes('chart') || t.includes('plot') || t.includes('graph') || t.includes('metric') || t.includes('table')) {
        charts++;
      }
    }
    return { total: comps.size, charts };
  }, [engine.effectiveComponents]);

  const subtitle = useMemo(() => {
    const parts: string[] = ['Interactive panel'];
    if (componentCount.charts > 0) {
      parts.push(`${componentCount.charts} chart${componentCount.charts > 1 ? 's' : ''}`);
    }
    return parts.join(' \u00B7 ');
  }, [componentCount.charts]);

  const handleDownload = useCallback(async (format: ArtifactExportFormat) => {
    const surface = getSurfaceElement();
    if (!surface || exportingFormat !== null) return;
    trackArtifactExported(format);
    setExportingFormat(format);
    try {
      const payload = await exportSurfaceSnapshot(surface, format);
      const stamp = new Date().toISOString().replaceAll(':', '-');
      downloadExportPayload(payload, `panel-${stamp}.${format}`);
    } finally {
      setExportingFormat(null);
    }
  }, [exportingFormat, getSurfaceElement]);

  if (!artifactActivated) return null;

  const showCard = !overlayOpen && !cardMinimized && hasComponents;

  return (
    <>
      {/* Floating preview card — visible when artifact exists, not minimized, overlay closed */}
      {showCard && (
        <div className="fixed bottom-20 left-3 right-3 z-30 desktop:hidden">
          <div className="relative flex w-full items-center rounded-2xl border border-white/[0.08] bg-zinc-900/90 shadow-2xl backdrop-blur-xl">
            <button
              type="button"
              onClick={onOpenOverlay}
              className="flex min-w-0 flex-1 items-center gap-4 py-4 pl-4 pr-2 text-left transition-all active:scale-[0.98]"
            >
              <IconFrame className="h-12 w-12 rounded-xl bg-zinc-800">
                <TrendingUp className="h-5 w-5 text-zinc-400" />
              </IconFrame>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-medium text-zinc-100">
                  View Full Visual Summary
                </p>
                <p className="mt-0.5 truncate text-[12px] text-zinc-500">
                  {subtitle}
                </p>
                <p className="mt-1 flex items-center gap-1 text-[11px] text-zinc-600">
                  <RotateCw className="h-3 w-3 animate-[wiggle_2s_ease-in-out_infinite]" />
                  Rotate for full-screen view
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={onMinimize}
              className="flex h-full items-center px-4 py-4 text-zinc-500 transition-colors active:text-zinc-300"
            >
              <IconFrame className="h-10 w-10 rounded-full border border-white/[0.1] bg-zinc-800">
                <X className="h-4 w-4" />
              </IconFrame>
            </button>
          </div>
        </div>
      )}

      {/* Full-page overlay */}
      {overlayOpen && (
        <div className="fixed inset-0 z-40 flex flex-col bg-zinc-950 text-zinc-100 desktop:hidden">
          {/* Header */}
          <header className="flex h-12 shrink-0 items-center justify-between border-b border-white/[0.06] px-3">
            <button
              type="button"
              onClick={onCloseOverlay}
              className="flex h-8 items-center gap-1.5 rounded-lg px-2 text-[13px] text-zinc-400 transition-colors active:bg-white/[0.06]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            <div className="flex items-center gap-1.5">
              <ConnectionStatus connected={connected} />
            </div>

            <div className="flex items-center gap-0.5">
              {hasComponents && (
                <ExportFormatButtons
                  formats={['png', 'pdf']}
                  disabled={exportingFormat !== null}
                  onDownload={handleDownload}
                  buttonClassName="h-7 rounded-lg px-2 active:bg-white/[0.06]"
                />
              )}
            </div>
          </header>

          {/* Page tabs */}
          {hasPages && (
            <ArtifactPageTabs
              pages={pages}
              viewingPageIndex={viewingPageIndex}
              liveComponentsSize={liveComponentsSize}
              onSelectPage={(index) => store.dispatch({ type: 'SET_VIEWING_PAGE', index })}
              onDeletePage={(idx) => void deletePage(idx)}
              classNames={{
                root: "shrink-0 px-2 py-1",
                tab: "px-2.5 py-1.5 pr-6 text-[12px]",
                tabInactive: "active:bg-white/[0.04]",
                delete: "h-5 w-5 active:text-zinc-300 active:bg-white/[0.08]",
                liveTab: "px-2.5 py-1.5 text-[12px]",
              }}
            />
          )}

          {/* Artifact content */}
          <ArtifactContent
            store={store}
            callbacks={callbacks}
            rootCuids={rootCuids}
            focusTarget={focusTarget}
            containerRef={containerRef}
            emptyState={
              <ArtifactEmptyState
                title="Panel"
                subtitle={connected
                  ? 'Waiting for the agent to render components...'
                  : 'Outputs, plots, and files will appear here'}
              />
            }
          />
        </div>
      )}
    </>
  );
}
